import React, { useCallback, useMemo, useState } from 'react';

import { useDebounce } from '@eika/hooks';

import { useInvalidateOutbox, useRemoveOutbox, useUnapproveOutboxItem } from '../../../../fetchHooks/useOutbox';
import type { Account, PaymentItemId, UniquePaymentId } from '../../../../generated-types';
import { OutboxWrapper } from '../../../../models/outbox';
import { OutboxStatus, OutboxViewFlow } from '../EditOutboxModal/types';
import ListItemDeleteFlow from './ListItemDeleteFlow';
import ListItemUnapproveFlow from './ListItemUnapproveFlow';
import OutboxView from './OutboxView';

import './Item.scss';

interface Props {
    outboxListItem: OutboxWrapper;
    checked: boolean;
    handleCheckboxSwitch?(): void;
    signSingleItem(itemId: PaymentItemId, outboxId: UniquePaymentId): void;
    disabled: boolean;
    fromAccount: Account | undefined;
    setModalState: (value: boolean) => void;
    isLoadingDetails: boolean;
}

const ListItem: React.FC<Props> = ({
    outboxListItem,
    checked,
    handleCheckboxSwitch,
    signSingleItem,
    disabled,
    fromAccount,
    setModalState,
    isLoadingDetails,
}) => {
    const invalidateOutbox = useInvalidateOutbox();

    const debouncedInvalidate = useDebounce(invalidateOutbox, 2500);

    const remove = useRemoveOutbox({ onSuccess: () => debouncedInvalidate() });
    const unapprove = useUnapproveOutboxItem({ onSuccess: () => debouncedInvalidate() });

    const [confirmState, setConfirmState] = useState<OutboxViewFlow>();

    const handleDelete = useCallback(() => remove.mutate(outboxListItem.paymentItemId), [remove, outboxListItem]);
    const handleUnapprove = useCallback(
        () => unapprove.mutate(outboxListItem.paymentItemId),
        [unapprove, outboxListItem],
    );

    const closeAndReset = useCallback(() => {
        unapprove.reset();
        remove.reset();
        setConfirmState(undefined);
    }, [setConfirmState, remove, unapprove]);

    const viewStatus: { flow: OutboxViewFlow; status: OutboxStatus } | undefined = useMemo(() => {
        if (remove.status !== 'idle' || confirmState === 'deleting') {
            return { flow: 'deleting', status: remove.status };
        }
        if (unapprove.status !== 'idle' || confirmState === 'unapproving') {
            return { flow: 'unapproving', status: unapprove.status };
        }

        return undefined;
    }, [remove, unapprove, confirmState]);

    if (viewStatus?.flow === 'deleting')
        return (
            <ListItemDeleteFlow
                status={viewStatus.status}
                onClose={closeAndReset}
                onDelete={handleDelete}
                confirmDeleteState={[confirmState === 'deleting', () => setConfirmState(undefined)]}
                outboxItemId={outboxListItem.uniqueId}
            />
        );

    if (viewStatus?.flow === 'unapproving')
        return (
            <ListItemUnapproveFlow
                status={viewStatus.status}
                onClose={closeAndReset}
                onUnapprove={handleUnapprove}
                confirmUnapproveState={[confirmState === 'unapproving', () => setConfirmState(undefined)]}
                outboxItemId={outboxListItem.uniqueId}
            />
        );

    return (
        <OutboxView
            fromAccount={fromAccount}
            outboxListItem={outboxListItem}
            checked={checked}
            handleCheckboxSwitch={handleCheckboxSwitch}
            onUnapproveClick={() => setConfirmState('unapproving')}
            onDeleteClick={() => setConfirmState('deleting')}
            onEditClick={() => setModalState(true)}
            disabled={disabled}
            signSingleItem={signSingleItem}
            isLoadingDetails={isLoadingDetails}
        />
    );
};

export default ListItem;
